import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Sparkles, ChevronRight, RefreshCcw, FileText, MessageSquare, Star, ShieldCheck } from 'lucide-react';
import {
  SPECIALTIES,
  SPECIALTY_LABELS,
  TASK_TYPE_LABELS,
  TASK_TYPE_HINTS,
  URGENCY_LEVELS,
  URGENCY_LABELS,
  type Specialty,
  type TaskType,
  type Urgency,
  type TriageResult,
} from '@parecer/shared';
import { AppShell } from '@/components/layout/AppShell';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import { api } from '@/lib/api-client';
import { cn, getInitials, formatBRL } from '@/lib/utils';

interface SuggestedLawyer {
  userId: string;
  name: string;
  oab?: string;
  oabState?: string;
  oabVerified?: boolean;
  bio?: string;
  specialties?: Specialty[];
  seniority?: string;
  averageRating?: number;
  totalReviews?: number;
  totalCompletedTasks?: number;
  pricing?: Array<{
    taskType: TaskType;
    basePrice: number;
    description?: string;
    estimatedDeliveryHours?: number;
    active?: boolean;
  }>;
}

interface TriageResponse {
  classification: TriageResult;
  suggestedLawyers: SuggestedLawyer[];
  model: string;
}

type Stage = 'describe' | 'review';

export function DemandaNova() {
  const [params] = useSearchParams();
  const preselectedLawyerId = params.get('lawyerId') ?? undefined;
  const preselectedTaskType = params.get('taskType') ?? undefined;

  const navigate = useNavigate();
  const { toast } = useToast();

  const [stage, setStage] = useState<Stage>('describe');
  const [description, setDescription] = useState('');
  const [preferredSpecialty, setPreferredSpecialty] = useState<Specialty | ''>('');
  const [triage, setTriage] = useState<TriageResponse | null>(null);
  // After triage runs, the client can override the model's choices.
  const [overrideTaskType, setOverrideTaskType] = useState<TaskType | ''>('');
  const [overrideUrgency, setOverrideUrgency] = useState<Urgency | ''>('');
  const [chosenLawyerId, setChosenLawyerId] = useState<string | undefined>(preselectedLawyerId);
  const [deadlineDays, setDeadlineDays] = useState<number | ''>('');

  // If we landed here from "Contratar este serviço" on a lawyer's profile,
  // pre-populate a sensible default description so the user can hit "Analisar"
  // immediately without having to invent prose.
  useEffect(() => {
    if (preselectedLawyerId && preselectedTaskType && !description) {
      const typeLabel = TASK_TYPE_LABELS[preselectedTaskType as TaskType] ?? preselectedTaskType;
      setDescription(
        `Quero contratar este advogado especificamente para: ${typeLabel.toLowerCase()}.\n\nDescreva aqui sua situação, objetivo e prazos.`,
      );
      setOverrideTaskType(preselectedTaskType as TaskType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const triageMut = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = { description };
      if (preferredSpecialty) body['preferredSpecialty'] = preferredSpecialty;
      if (preselectedLawyerId) body['preferredLawyerId'] = preselectedLawyerId;
      return api.post<TriageResponse>('/tasks/triage', body);
    },
    onSuccess: (data) => {
      setTriage(data);
      setOverrideTaskType((prev) => (prev ? prev : data.classification.taskType) as TaskType);
      setOverrideUrgency(data.classification.urgency);
      if (preselectedLawyerId) setChosenLawyerId(preselectedLawyerId);
      setStage('review');
    },
    onError: (err) =>
      toast({
        variant: 'danger',
        title: 'Falha ao analisar',
        description: err instanceof Error ? err.message : 'Erro inesperado',
      }),
  });

  const createMut = useMutation({
    mutationFn: async () => {
      if (!triage) throw new Error('Triage não executada');
      const finalTaskType = overrideTaskType || triage.classification.taskType;
      const body: Record<string, unknown> = {
        type: finalTaskType,
        description,
        urgency: overrideUrgency || triage.classification.urgency,
        recommendedSpecialty: triage.classification.recommendedSpecialty,
        triageMeta: {
          sourceDescription: description,
          classifiedAt: new Date().toISOString(),
          model: triage.model,
          confidence: triage.classification.confidence,
        },
      };
      if (chosenLawyerId) body['lawyerId'] = chosenLawyerId;
      if (typeof deadlineDays === 'number' && deadlineDays > 0) body['deadlineDays'] = deadlineDays;
      return api.post<{ taskId: string; status: string }>('/tasks', body);
    },
    onSuccess: (data) => {
      toast({
        variant: 'success',
        title: 'Demanda criada',
        description: chosenLawyerId
          ? 'Advogado escolhido será notificado.'
          : 'Sua demanda foi para o marketplace — advogados podem aceitar.',
      });
      navigate(`/app/tarefas/${data.taskId}`);
    },
    onError: (err) =>
      toast({
        variant: 'danger',
        title: 'Falha ao criar demanda',
        description: err instanceof Error ? err.message : 'Erro inesperado',
      }),
  });

  const finalTaskType: TaskType | undefined =
    (overrideTaskType as TaskType) || triage?.classification.taskType;
  const needsDocument =
    finalTaskType ? TASK_TYPE_HINTS[finalTaskType]?.needsDocument : 'optional';

  const chosenLawyer = useMemo(
    () => triage?.suggestedLawyers.find((l) => l.userId === chosenLawyerId),
    [triage, chosenLawyerId],
  );

  // Lawyer's matching pricing for the chosen type, when available.
  const chosenLawyerPricing = useMemo(() => {
    if (!chosenLawyer || !finalTaskType) return undefined;
    return chosenLawyer.pricing?.find((p) => p.taskType === finalTaskType && p.active !== false);
  }, [chosenLawyer, finalTaskType]);

  return (
    <AppShell role="users">
      <div className="max-w-4xl">
        <h1 className="font-serif text-display-lg">Nova demanda</h1>
        <p className="text-muted-foreground mt-2">
          Descreva o que você precisa — nossa IA classifica e indica advogados especializados.
          Documento não é obrigatório.
        </p>

        {/* === STEP 1 — Describe === */}
        {stage === 'describe' && (
          <Card className="mt-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-accent" />
                O que você precisa?
              </CardTitle>
              <CardDescription>
                Conte sua situação em linguagem natural. Ex: "Vou abrir uma startup com dois sócios e
                preciso entender como dividir as quotas e proteger meu IP", ou "Estou me separando e
                quero saber como ficam a guarda e a pensão dos filhos".
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="description">Descrição</Label>
                <textarea
                  id="description"
                  rows={8}
                  className="mt-1.5 flex w-full rounded-md border border-input bg-surface px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  placeholder="Descreva o problema, contexto, prazos relevantes e o que você espera como resultado..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  maxLength={5000}
                />
                <div className="mt-1 flex items-center justify-between text-[11px] text-muted-foreground">
                  <span>{description.length} / 5000 caracteres (mínimo 20)</span>
                </div>
              </div>

              <div>
                <Label htmlFor="preferredSpecialty">Área jurídica preferida (opcional)</Label>
                <select
                  id="preferredSpecialty"
                  className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-surface px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  value={preferredSpecialty}
                  onChange={(e) => setPreferredSpecialty(e.target.value as Specialty | '')}
                >
                  <option value="">A IA decide pra mim</option>
                  {SPECIALTIES.map((s) => (
                    <option key={s} value={s}>{SPECIALTY_LABELS[s]}</option>
                  ))}
                </select>
                <p className="text-[11px] text-muted-foreground mt-1">
                  Sabe a área? Indique pra refinar a sugestão. Se não souber, a IA classifica.
                </p>
              </div>

              {preselectedLawyerId && (
                <div className="rounded-md border border-accent/30 bg-accent/5 p-3 text-sm">
                  <p className="font-medium text-accent">Advogado já escolhido</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Você veio do perfil de um advogado específico. A análise vai direcionar a demanda
                    pra ele(a) — sem buscar outros profissionais.
                  </p>
                </div>
              )}

              <Button
                variant="accent"
                onClick={() => triageMut.mutate()}
                disabled={description.trim().length < 20}
                loading={triageMut.isPending}
                className="w-full gap-2"
              >
                <Sparkles className="h-4 w-4" />
                Analisar com IA
              </Button>

              <details className="text-xs">
                <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                  Já tenho um documento pra revisar
                </summary>
                <p className="mt-2 text-muted-foreground">
                  Você pode subir o contrato direto:{' '}
                  <a href="/app/contratos/novo" className="text-accent hover:underline">
                    /app/contratos/novo
                  </a>{' '}
                  — a IA analisa o documento e cria a demanda automaticamente.
                </p>
              </details>
            </CardContent>
          </Card>
        )}

        {/* === STEP 2 — Review triage + choose lawyer === */}
        {stage === 'review' && triage && (
          <>
            <Card className="mt-8">
              <CardHeader>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Sparkles className="h-5 w-5 text-accent" /> Análise da IA
                    </CardTitle>
                    <CardDescription>
                      Confiança: {Math.round(triage.classification.confidence * 100)}%.
                      Você pode editar a classificação se discordar.
                    </CardDescription>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setStage('describe')}
                    className="gap-1 text-xs"
                  >
                    <RefreshCcw className="h-3 w-3" /> Refinar descrição
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="rounded-md bg-muted/30 p-3 text-sm">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium mb-1">
                    Por quê?
                  </p>
                  <p>{triage.classification.reasoning}</p>
                </div>

                <p className="text-[11px] text-muted-foreground rounded-md border border-border/40 bg-muted/10 px-3 py-2">
                  Esta classificação é uma <strong>sugestão</strong> baseada na sua descrição. Não
                  cria relação cliente-advogado, não substitui consulta profissional. Você pode
                  ajustar abaixo antes de criar a demanda.
                </p>

                <div className="grid sm:grid-cols-3 gap-3">
                  <div>
                    <Label htmlFor="taskType">Tipo de serviço</Label>
                    <select
                      id="taskType"
                      className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-surface px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      value={overrideTaskType}
                      onChange={(e) => setOverrideTaskType(e.target.value as TaskType)}
                    >
                      {Object.keys(TASK_TYPE_LABELS).map((t) => (
                        <option key={t} value={t}>{TASK_TYPE_LABELS[t as TaskType]}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="urgency">Urgência</Label>
                    <select
                      id="urgency"
                      className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-surface px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      value={overrideUrgency}
                      onChange={(e) => setOverrideUrgency(e.target.value as Urgency)}
                    >
                      {URGENCY_LEVELS.map((u) => (
                        <option key={u} value={u}>{URGENCY_LABELS[u]}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>Área jurídica</Label>
                    <p className="mt-1.5 h-10 flex items-center px-3 rounded-md bg-muted/30 text-sm">
                      {SPECIALTY_LABELS[triage.classification.recommendedSpecialty]}
                    </p>
                  </div>
                </div>

                {needsDocument === 'required' && (
                  <div className="rounded-md border border-warning/40 bg-warning/5 p-3 text-sm flex items-start gap-2">
                    <FileText className="h-4 w-4 text-warning mt-0.5 flex-none" />
                    <p>
                      <strong>Documento necessário.</strong> Esse tipo de serviço exige que você
                      anexe o contrato ou documento — o advogado pedirá após aceitar a demanda.
                    </p>
                  </div>
                )}

                {triage.classification.followUpQuestions.length > 0 && (
                  <div className="rounded-md border border-accent/30 bg-accent/5 p-3 text-sm">
                    <p className="text-xs text-accent uppercase tracking-wide font-medium mb-2">
                      A IA tem dúvidas
                    </p>
                    <ul className="space-y-1 list-disc pl-5">
                      {triage.classification.followUpQuestions.map((q, i) => (
                        <li key={i}>{q}</li>
                      ))}
                    </ul>
                    <p className="text-xs text-muted-foreground mt-2">
                      Volte e adicione esses detalhes na descrição pra obter um match melhor.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Lawyer list */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle>
                  {preselectedLawyerId
                    ? 'Advogado selecionado'
                    : `Advogados sugeridos (${triage.suggestedLawyers.length})`}
                </CardTitle>
                <CardDescription>
                  {preselectedLawyerId
                    ? 'Você veio do perfil deste advogado.'
                    : 'Filtrados por especialidade, com OAB verificada no topo. Você pode também publicar pra todos ("marketplace aberto").'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {triage.suggestedLawyers.length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    Nenhum advogado encontrado pra essa especialidade ainda. Sua demanda pode ir
                    pro marketplace aberto — qualquer advogado da área poderá aceitar.
                  </p>
                )}
                {triage.suggestedLawyers.map((l) => {
                  const chosen = chosenLawyerId === l.userId;
                  const matchingPrice = l.pricing?.find(
                    (p) => p.taskType === finalTaskType && p.active !== false,
                  );
                  return (
                    <button
                      key={l.userId}
                      type="button"
                      onClick={() => setChosenLawyerId(chosen ? undefined : l.userId)}
                      className={cn(
                        'w-full text-left rounded-md border p-4 transition-colors',
                        chosen
                          ? 'border-accent bg-accent/10'
                          : 'border-border hover:border-accent/40',
                      )}
                    >
                      <div className="flex items-start gap-3">
                        <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center text-xs font-medium flex-none">
                          {getInitials(l.name)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium">{l.name}</span>
                            {l.oabVerified && (
                              <Badge variant="success" className="gap-1 text-[10px]">
                                <ShieldCheck className="h-3 w-3" /> Verificado
                              </Badge>
                            )}
                            {l.seniority && (
                              <Badge variant="accent" className="text-[10px]">{l.seniority}</Badge>
                            )}
                            {chosen && <Badge variant="accent" className="text-[10px]">Selecionado</Badge>}
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            OAB/{l.oabState ?? '—'} {l.oab ?? ''}
                            {l.averageRating !== undefined && (
                              <>
                                {' · '}
                                <span className="inline-flex items-center gap-0.5">
                                  <Star className="h-3 w-3 fill-accent text-accent" />
                                  {l.averageRating.toFixed(1)} ({l.totalReviews ?? 0})
                                </span>
                              </>
                            )}
                            {(l.totalCompletedTasks ?? 0) > 0 && <> · {l.totalCompletedTasks} entregas</>}
                          </p>
                          {l.bio && (
                            <p className="text-xs text-muted-foreground mt-1.5 line-clamp-2">{l.bio}</p>
                          )}
                        </div>
                        {matchingPrice && (
                          <div className="text-right flex-none">
                            <p className="font-serif text-base">
                              {formatBRL(Math.round(matchingPrice.basePrice * 1.15))}
                            </p>
                            <p className="text-[10px] text-muted-foreground">com taxa plataforma</p>
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}

                {!preselectedLawyerId && (
                  <button
                    type="button"
                    onClick={() => setChosenLawyerId(undefined)}
                    className={cn(
                      'w-full text-left rounded-md border p-3 transition-colors',
                      !chosenLawyerId
                        ? 'border-accent bg-accent/10'
                        : 'border-dashed border-border hover:border-accent/40',
                    )}
                  >
                    <div className="flex items-center gap-3 text-sm">
                      <MessageSquare className="h-4 w-4 text-muted-foreground" />
                      <span>
                        <strong>Não escolher agora</strong> — publica no marketplace aberto, advogados
                        da área aceitam por iniciativa.
                      </span>
                    </div>
                  </button>
                )}
              </CardContent>
            </Card>

            {/* Final confirmation */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle>Detalhes finais</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="deadlineDays">Prazo desejado (dias, opcional)</Label>
                  <Input
                    id="deadlineDays"
                    type="number"
                    min={1}
                    max={60}
                    className="mt-1.5 max-w-[180px]"
                    value={deadlineDays}
                    onChange={(e) => {
                      const v = e.target.value;
                      setDeadlineDays(v === '' ? '' : Number(v));
                    }}
                  />
                </div>
                {chosenLawyer && chosenLawyerPricing && (
                  <div className="rounded-md border border-border/60 bg-muted/20 p-3 text-sm">
                    <p className="text-muted-foreground">Você vai pagar:</p>
                    <p className="font-serif text-xl mt-1">
                      {formatBRL(Math.round(chosenLawyerPricing.basePrice * 1.15))}
                    </p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      85% vai pro advogado · 15% taxa da plataforma · escrow via Pagar.me
                    </p>
                  </div>
                )}
                <Button
                  variant="accent"
                  size="lg"
                  className="w-full gap-2"
                  loading={createMut.isPending}
                  onClick={() => createMut.mutate()}
                >
                  Criar demanda
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <p className="text-[11px] text-muted-foreground text-center">
                  Pagamento só rola depois que o advogado aceitar e você confirmar.
                </p>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </AppShell>
  );
}
